import React, { useEffect, useState } from "react";
import { Tab, Box, Button, Grid, Paper, Typography, Tabs } from "@mui/material";
import {
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
  SwitchElement,
} from "react-hook-form-mui";
import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMProject } from "../graphql/queries";
import { GetLLMProjectQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { updateLLMProjectAfterEdit } from "../dataaccess/updateProjectAfterEdit";
import { useUser } from "../contexts/useUser";
import { useLLM } from "llmasaservice-client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const initialValues = {
  name: "",
  status: "active",
  description: "",
  newCustomerTokenCount: 1000,
  cacheResponsesForSeconds: 3600,
  promptBlacklist: "",
  loadSheddingTier: 0,
  newCustomerLoadSheddingTier: 0,
  maintenanceMode: false,
  maintenanceModeMessage:
    "We are currently performing maintenance on this service. Please try again later.",
  loadBalancingMode: "failover",
  systemInstructions:
    "Please follow these guidelines when generating responses:\n1. **Respect and Inclusivity**: Be respectful and avoid hate speech, racism, sexism, or discriminatory language. Use gender-neutral terms and avoid assumptions about personal characteristics.\n2. **Safety and Appropriateness**: Ensure responses are safe and suitable for all audiences; avoid harmful or dangerous advice.\n3. **Accuracy**: Provide accurate information. If unsure, indicate the need for more information or state the uncertainty.\n4. **Confidentiality**: Do not share personal or sensitive information; respect privacy and confidentiality.",
  mcpServerWhitelist: "",
};

const schema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  status: z.string().min(1, "Status is required"),
  description: z.string().optional().nullable(),
  newCustomerTokenCount: z.coerce
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  cacheResponsesForSeconds: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),

  promptBlacklist: z.string().optional().nullable(),

  loadSheddingTier: z.coerce.number().int().nonnegative(),
  newCustomerLoadSheddingTier: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .nullable(),

  maintenanceMode: z.boolean(),
  maintenanceModeMessage: z.string().optional().nullable(),

  loadBalancingMode: z.string(),

  systemInstructions: z.string().optional().nullable(),

  mcpServerWhitelist: z.string().optional().nullable(),
});

type ProjectFormType = z.infer<typeof schema>;

const ProjectForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const { trialDaysRemaining, subscriptionActive } = useUser();
  const [tab, setTab] = useState("general");
  const [runningAudit, setRunningAudit] = useState(false);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const { send, idle, response } = useLLM({ project_id: id ?? "", url: url });

  useEffect(() => {
    if (idle) setRunningAudit(false);
  }, [idle]);

  const queryClient = useQueryClient();
  const mutationUpdateProject = useMutation({
    mutationFn: updateLLMProjectAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating project", error as any);
    },
  });

  const {
    data: projectData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMProject, { id: id })) as Promise<
        GraphQLResult<GetLLMProjectQuery>
      >,
    enabled: !!id,
  });

  const { handleSubmit, control, reset, watch } = useForm<ProjectFormType>({
    values: projectData?.data?.getLLMProject || initialValues,
    resolver: zodResolver(schema),
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const handleUpdate = (values: ProjectFormType) => {
    if (!values) return;

    try {
      mutationUpdateProject.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating project", error as any);
    }
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: ProjectFormType) => {
    if (id) handleUpdate(data);
  };

  const mcpServerWhitelistWatch = watch("mcpServerWhitelist");

  const mcpAuditClick = async () => {
    setRunningAudit(true);
    try {
      let auditPrompt = `**Task**
You are a security-focused remote MCP-server auditor.
For each server below, perform the checks in Section A and return your findings in the output format in Section B.
Important: You don't have access to the code, so you must rely on the tool list and descriptions to identify potential issues.
      
**Section A – Required checks**

1. **Server reachability**
For each server URL:
- Check if the server is reachable and return a valid HTTP response.
- Check if the server has a valid tool list.

2. **Tool-list integrity**
For each tool object:
= "name" and "description" make sense and clearly describe the tool's purpose.
- "name" ⇄ "description" **consistency** – do they describe the same capability?
- **Parameter safety** – look for:
  - unrestricted arbitrary URL fetches, shell/SQL execution, broad file I/O, etc.
  - missing "additionalProperties:false" or over-permissive schemas.
- **Input validation** – are types, ranges, and defaults reasonable?
- **Security-specific red flags**
  - Secrets in descriptions, hard-coded keys, obvious PII collection.
  - Network egress abilities that could exfiltrate data.

**Section B – Output format**
Add no preamble or summary, just follow this format for each server:

Server: **[Server URL]** \n
Reachable: ✅ or ❌ \n
Risk: [🟢 Low 🟡 Medium 🔴 High] \n
Tool count: [number of tools] \n 

[Tool name] - [Tool description]
[Summary of findings]

[Tool name] - [Tool description]
[Summary of findings]
...`;

      // iterate the projects whitelist of mcp servers
      const mcpServers = mcpServerWhitelistWatch
        ? mcpServerWhitelistWatch
            .split(/[\n,|]+/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      for (const server of mcpServers) {
        try {
          // first check it is a valid url
          try {
            new URL(server);
          } catch (err) {
            auditPrompt += `\n\n**${server}**\n\nInvalid URL`;
            continue;
          }

          // get the standardized tool list
          const result = await API.get(
            "llmpublicapi",
            `/tools/${encodeURIComponent(server)}`,
            {}
          );

          if (!result || result === "") {
            auditPrompt += `\n\n**${server}**\n\nUnreachable URL`;
            continue;
          }

          auditPrompt += `\n\n**${server}**\n\n${JSON.stringify(
            result,
            null,
            2
          )}`;
        } catch (err) {
          auditPrompt += `\n\n**${server}**\n\nError: ${err}`;
          continue;
        }
      }

      console.log("Audit prompt: ", auditPrompt);

      send(auditPrompt);
    } catch (err) {
      console.error("Error auditing MCP servers: ", err);
      setRunningAudit(false);
    }
  };

  return (
    <Paper
      sx={{ margin: 2, padding: 2, borderRadius: 3, border: "1px solid #444" }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        aria-label="Project form tabs"
      >
        <Tab label="Project Settings" value="general" />
        <Tab label="Security" value="security" />
      </Tabs>

      {tab === "general" && (
        <Grid container padding={2} spacing={2}>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              control={control}
              name="name"
              label="Name"
              size="small"
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectElement
              control={control}
              name="status"
              label="Project status"
              size="small"
              required
              fullWidth
              options={[
                { id: "active", label: "Active" },
                { id: "disabled", label: "Disabled" },
                {
                  id: "errors",
                  label: "Disabled (due to errors)",
                },
                { id: "deleted", label: "Deleted" },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldElement
              control={control}
              name="description"
              label="Description"
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextareaAutosizeElement
              control={control}
              name="systemInstructions"
              label="System Instructions (use to always include brand and policy information to prompts sent to LLMs)"
              size="small"
              fullWidth
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <hr />
            <Typography variant="caption">operational settings</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <SelectElement
              control={control}
              name="cacheResponsesForSeconds"
              label="Response Caching"
              size="small"
              required
              fullWidth
              options={[
                { id: 0, label: "Off" },
                { id: 60, label: "1 minute" },
                {
                  id: 900,
                  label: "15 minutes",
                },
                { id: 1800, label: "30 minutes" },
                { id: 3600, label: "1 hour" },
                { id: 86400, label: "1 day" },
                { id: 604800, label: "1 week" },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SelectElement
              control={control}
              name="loadBalancingMode"
              label="Load Balancing / Failover"
              size="small"
              required
              fullWidth
              options={[
                {
                  id: "off",
                  label: "No failover",
                },
                {
                  id: "failover",
                  label: "Try other active services if first attempt fails.",
                },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectElement
              control={control}
              name="loadSheddingTier"
              label="Load Shedding Tier "
              size="small"
              required
              fullWidth
              options={[
                { id: "0", label: "All customer tiers are processed" },
                { id: "1", label: "Shed Tier 1 customers" },
                {
                  id: "2",
                  label: "Shed Tier 1 and Tier 2 customers",
                },
                { id: "3", label: "Shed Tier 1, 2 and 3 customers" },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}></Grid>
          <Grid item xs={3}>
            <SwitchElement
              control={control}
              name="maintenanceMode"
              label={
                !subscriptionActive && trialDaysRemaining <= 0
                  ? "Maintenance Mode (trial expired)"
                  : "Maintenance Mode"
              }
              disabled={!subscriptionActive && trialDaysRemaining <= 0}
            />
          </Grid>

          <Grid item xs={9}>
            <TextareaAutosizeElement
              control={control}
              name="maintenanceModeMessage"
              label="Maintenance Mode Message"
              size="small"
              fullWidth
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <hr />
            <Typography variant="caption">new customer settings</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectElement
              control={control}
              name="newCustomerLoadSheddingTier"
              label="New Customer Default Load Shedding Tier "
              size="small"
              required
              fullWidth
              options={[
                { id: 1, label: "Tier 1 (first to shed)" },
                {
                  id: 2,
                  label: "Tier 2 (customers shed after Tier 1)",
                },
                { id: 3, label: "Tier 3 customers (shed as last resort)" },
                { id: 4, label: "Never shed" },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldElement
              control={control}
              name="newCustomerTokenCount"
              label="New Customer Initial Token Count Grant (blank for none)"
              size="small"
              type="number"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <hr />
          </Grid>
        </Grid>
      )}

      {tab === "security" && (
        <Grid container padding={2} spacing={2}>
          <Grid item xs={12}>
            <TextareaAutosizeElement
              control={control}
              name="mcpServerWhitelist"
              label="Server Whitelist (one per line)"
              size="small"
              fullWidth
              rows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              disabled={
                runningAudit ||
                !mcpServerWhitelistWatch ||
                mcpServerWhitelistWatch === ""
              }
              onClick={mcpAuditClick}
            >
              Test and Audit MCP Servers
            </Button>

            {runningAudit && (
              <Typography variant="caption">
                {" "}
                Running audit... Please wait.
              </Typography>
            )}

            {response && (
              <Box
                sx={{
                  width: "100%",
                  height: 600,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  mt: 2,
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {response}
                </ReactMarkdown>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default ProjectForm;
